import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router';

import type { UserLanguage } from '~/api/user/types';

import { useLogin, useLogout } from '~/api/auth';
import { userQueryOptions } from '~/api/user/useUser';
import { useCraftLocalStorage } from '~/hooks/useCraftLocalStorage';
import { CommandMenuItem } from '~/providers/CommandMenuProvider/CommandMenuItem';
import { routes } from '~/providers/RouterProvider/router.routes';

export const RootCommandMenuItems = () => {
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const emailAddress = (import.meta.env.VITE_LOCAL_DEV_EMAIL as string) || null;
  const password = (import.meta.env.VITE_LOCAL_DEV_PASSWORD as string) || null;

  const { i18n } = useTranslation();
  const [originalLanguage, setOriginalLanguage] = useState<UserLanguage>(i18n.language as UserLanguage);

  const [, setLanguage] = useCraftLocalStorage('language');
  const languages = ['nl', 'fr', 'en'] satisfies UserLanguage[];
  const nextLanguage = languages[(languages.indexOf(i18n.language as UserLanguage) + 1) % languages.length];
  const isCiMode = i18n.language === 'cimode';

  const navigate = useNavigate();
  const isDesignSystemRoute = !!useMatch(routes.designSystem);

  const queryClient = useQueryClient();
  // A non-subscribing read on purpose: subscribing here would put an observer on the user query
  // outside the protected tree, which would refetch against a dead session after a logout.
  const isAuthenticated = queryClient.getQueryData(userQueryOptions.queryKey) !== undefined;

  return (
    <>
      <CommandMenuItem
        group="General"
        label={isCiMode ? 'Hide translation keys' : 'Show translation keys'}
        onClick={() => {
          if (!isCiMode) setOriginalLanguage(i18n.language as UserLanguage);
          setLanguage(isCiMode ? originalLanguage : ('cimode' as UserLanguage));
        }}
        shortcut="K"
      />

      <CommandMenuItem group="General" label={`Switch language (${nextLanguage})`} onClick={() => setLanguage(nextLanguage)} shortcut="L" />

      <CommandMenuItem
        group="General"
        label={isDesignSystemRoute ? 'Go back to Craft' : 'Go to design system'}
        onClick={() => navigate(isDesignSystemRoute ? routes.dashboard : routes.designSystem)}
        shortcut="D"
      />

      <CommandMenuItem
        group="General"
        label="Refetch active queries"
        onClick={() => queryClient.refetchQueries({ type: 'active' })}
        shortcut="Q"
      />

      {!isAuthenticated && emailAddress && password && (
        <CommandMenuItem
          group="General"
          label="Log in"
          onClick={() =>
            loginMutation.mutate({ emailAddress, password }, { onSuccess: () => navigate(routes.dashboard, { replace: true }) })
          }
          shortcut="X"
        />
      )}

      {isAuthenticated && <CommandMenuItem group="General" label="Log out" onClick={() => logoutMutation.mutate()} shortcut="X" />}

      {isCiMode && (
        <style>
          {`
            * {
              word-break: break-all !important;
              text-transform: none !important;
            }
          `}
        </style>
      )}
    </>
  );
};
